import { useMutation } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues, uploadFile } from '../api';
import { parsePln } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { KINDS, type ItemKind } from './types';
import { useUnsavedGuard, validForm } from '../ui/unsaved';

type ManualKind = Extract<ItemKind, 'wydatek' | 'reczna' | 'potracenie'>;

/**
 * "Dodaj pozycję" of a settlement: an expense (with a photo of the receipt — on a phone straight from the camera), a
 * manual item or a deduction. `onlyExpenses`: a person adding to their own settlement (scope own) adds expenses only.
 */
export function ExpenseForm({ settlementId, onlyExpenses, onAdded }: { settlementId: string; onlyExpenses: boolean; onAdded: () => void }) {
  const [kind, setKind] = useState<ManualKind>('wydatek');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [local, setLocal] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  // the id of the photo once uploaded: a retry after a failed save does not upload it again
  const uploaded = useRef<{ file: File; id: string } | null>(null);
  const add = useMutation({
    mutationFn: async (amountGrosze: number) => {
      if (kind === 'wydatek' && photo && uploaded.current?.file !== photo) uploaded.current = { file: photo, id: await uploadFile(photo) };
      const receiptFileId = kind === 'wydatek' && photo ? uploaded.current!.id : undefined;
      return api(`/api/settlements/${settlementId}/items`, { method: 'POST', body: { kind, amountGrosze, description: description.trim(), ...(receiptFileId ? { receiptFileId } : {}) } });
    },
    onSuccess: () => {
      setDescription('');
      setAmount('');
      setPhoto(null);
      uploaded.current = null;
      if (fileInput.current) fileInput.current.value = '';
      onAdded();
    },
  });
  function grosze(): number | null {
    try {
      const g = parsePln(amount);
      setLocal('');
      return g;
    } catch (err) {
      setLocal((err as Error).message);
      return null;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const g = grosze();
    if (g !== null) add.mutate(g);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: description.trim() !== '' || amount.trim() !== '' || photo !== null,
    save: async (el) => {
      const g = grosze();
      if (g === null || !validForm(el)) return false;
      await add.mutateAsync(g);
      return true;
    },
  });
  const error = local || (add.error instanceof ApiError ? (fieldIssues(add.error)[0]?.message ?? add.error.message) : add.error ? 'Nie udało się dodać' : '');
  const kinds: ManualKind[] = onlyExpenses ? ['wydatek'] : ['wydatek', 'reczna', 'potracenie'];
  return (
    <form ref={guardRef} className="fin-add stl-add" aria-label="Dodaj pozycję" data-feature-id="settlements.expenses" onSubmit={submit}>
      <label className="field">
        <span>Rodzaj</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as ManualKind)}>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {KINDS[k]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Opis</span>
        <input value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} placeholder={kind === 'wydatek' ? 'np. paliwo, parking' : ''} autoComplete="off" />
      </label>
      <label className="field">
        <span>Kwota (zł)</span>
        <NumberInput kind="money" value={amount} onValueChange={setAmount} />
      </label>
      {kind === 'wydatek' && (
        <label className="field">
          <span>Zdjęcie paragonu</span>
          <input ref={fileInput} type="file" accept="image/*,application/pdf" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
        </label>
      )}
      <button className="btn" type="submit" disabled={add.isPending}>
        {add.isPending ? 'Zapisywanie…' : 'Dodaj'}
      </button>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
